import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  getInscripciones, createInscripcion, updateInscripcion, getInscripcion,
  uploadFotoInscripcion, aprobarInscripcion, integrarInscripcion, generarCredencial,
  getEstadisticasInscripciones, bulkUploadInscripciones, exportInscripcionesExcel,
  downloadInscripcionesTemplate, getCiclos, promoverCicloEscolar,
  precargarInscripcionByCurp, comprobanteInscripcion, deleteInscripcion, crearReinscripcion
} from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { 
  Plus, MagnifyingGlass, Upload, Download, Eye, PencilSimple, Trash,
  Check, X, Student, FileXls, IdentificationCard, ArrowsClockwise,
  UserPlus, Funnel, Warning, CheckCircle, Clock, XCircle, Users, Printer, MagicWand
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';

const TIPOS_SANGRE = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const ESTADOS_CIVILES = [
  { value: 'soltero', label: 'Soltero(a)' },
  { value: 'casado', label: 'Casado(a)' },
  { value: 'divorciado', label: 'Divorciado(a)' },
  { value: 'viudo', label: 'Viudo(a)' },
  { value: 'union_libre', label: 'Unión libre' }
];

const InscripcionesPage = () => {
  const { user, hasPermission, hasModuleAccess } = useAuth();
  const canEditInscripciones = hasModuleAccess('inscripciones');
  const canDelete = hasPermission(['administrador', 'direccion']);
  const [inscripciones, setInscripciones] = useState([]);
  const [ciclos, setCiclos] = useState([]);
  const [selectedCiclo, setSelectedCiclo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showPromocionModal, setShowPromocionModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showReinscripcionModal, setShowReinscripcionModal] = useState(false);
  const [reinsCurp, setReinsCurp] = useState('');
  const [reinsPreload, setReinsPreload] = useState(null);
  const [reinsLoading, setReinsLoading] = useState(false);
  const [reinsGrupo, setReinsGrupo] = useState('A');
  const [selectedInscripcion, setSelectedInscripcion] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState('datos');
  const [estadisticas, setEstadisticas] = useState(null);
  const [promocionPreview, setPromocionPreview] = useState(null);
  const [busqueda, setBusqueda] = useState('');

  const [filters, setFilters] = useState({
    turno: '',
    grupo: '',
    estatus: '',
    incluir_egresados: false,
  });

  const initialFormData = {
    // Datos del alumno
    matricula: '',
    nombre: '',
    apellido_paterno: '',
    apellido_materno: '',
    curp: '',
    fecha_nacimiento: '',
    nacionalidad: 'Mexicana',
    estatura: '',
    peso: '',
    domicilio: '',
    colonia: '',
    codigo_postal: '',
    municipio: '',
    telefono_contacto: '',
    tipo_sangre: '',
    escuela_procedencia: '',
    promedio_primaria: '',
    condiciones_medicas: '',
    turno: '',
    grupo: '',
    ciclo_escolar_id: '',
    // Tutor
    tutor_nombre: '',
    tutor_apellidos: '',
    tutor_escolaridad: '',
    tutor_ocupacion: '',
    tutor_estado_civil: '',
    tutor_email: '',
    tutor_telefono_casa: '',
    tutor_telefono_celular: '',
    tutor_direccion: '',
    tutor_colonia: '',
    tutor_municipio: '',
    tutor_codigo_postal: '',
    // Emergencia
    emergencia_nombre: '',
    emergencia_parentesco: '',
    emergencia_telefono: '',
    emergencia_traslado: ''
  };

  const [formData, setFormData] = useState(initialFormData);
  const [fotoFile, setFotoFile] = useState(null);
  const [fotoPreview, setFotoPreview] = useState(null);

  const fetchCiclos = useCallback(async () => {
    try {
      const response = await getCiclos();
      setCiclos(response.data);
      const activo = response.data.find(c => c.activo);
      if (activo) {
        setSelectedCiclo(activo);
        setFormData(prev => ({ ...prev, ciclo_escolar_id: activo.id }));
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error fetching ciclos:', error);
      }
    }
  }, []);

  const fetchInscripciones = useCallback(async () => {
    if (!selectedCiclo) return;
    
    setLoading(true);
    try {
      const params = { 
        page, 
        limit: 50,
        ciclo_escolar_id: selectedCiclo.id
      };
      
      if (busqueda) params.busqueda = busqueda;
      Object.entries(filters).forEach(([key, value]) => {
        if (value === true) params[key] = true;
        else if (value && value !== 'all') params[key] = value;
      });
      
      const response = await getInscripciones(params);
      setInscripciones(response.data.inscripciones);
      setTotal(response.data.total);
      
      // Cargar estadísticas
      const statsRes = await getEstadisticasInscripciones(selectedCiclo.id);
      setEstadisticas(statsRes.data);
    } catch (error) {
      toast.error('Error al cargar inscripciones');
    } finally {
      setLoading(false);
    }
  }, [selectedCiclo, page, filters, busqueda]);

  useEffect(() => {
    fetchCiclos();
  }, [fetchCiclos]);

  useEffect(() => {
    if (selectedCiclo) {
      fetchInscripciones();
    }
  }, [selectedCiclo, fetchInscripciones]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({ turno: '', grupo: '', estatus: '' });
    setBusqueda('');
    setPage(1);
  };

  const validateCURP = (curp) => {
    const pattern = /^[A-Z]{4}\d{6}[HM][A-Z]{5}[A-Z0-9]\d$/;
    return pattern.test(curp.toUpperCase());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validaciones
    if (!validateCURP(formData.curp)) {
      toast.error('CURP inválido. Formato: 4 letras + 6 dígitos + H/M + 5 letras + 1 alfanumérico + 1 dígito');
      return;
    }
    
    if (parseFloat(formData.promedio_primaria) < 0 || parseFloat(formData.promedio_primaria) > 10) {
      toast.error('El promedio debe estar entre 0 y 10');
      return;
    }
    
    try {
      const dataToSend = {
        ...formData,
        curp: formData.curp.toUpperCase(),
        promedio_primaria: parseFloat(formData.promedio_primaria)
      };
      
      let inscripcionId;
      
      if (editMode && selectedInscripcion) {
        await updateInscripcion(selectedInscripcion.id, dataToSend);
        inscripcionId = selectedInscripcion.id;
        toast.success('Inscripción actualizada correctamente');
      } else {
        const response = await createInscripcion(dataToSend);
        inscripcionId = response.data.id;
        toast.success('Inscripción creada correctamente');
      }
      
      // Subir foto si existe
      if (fotoFile && inscripcionId) {
        try {
          await uploadFotoInscripcion(inscripcionId, fotoFile);
          toast.success('Foto subida correctamente');
        } catch (error) {
          toast.error('Error al subir la foto');
        }
      }
      
      setShowModal(false);
      resetForm();
      fetchInscripciones();

      // Auto-descargar comprobante en alta nueva (no en edición)
      if (!editMode && inscripcionId) {
        try {
          const compResp = await comprobanteInscripcion(inscripcionId);
          const blob = new Blob([compResp.data], { type: 'application/pdf' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `comprobante_inscripcion_${dataToSend.matricula}.pdf`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
          toast.success('Comprobante de inscripción descargado');
        } catch (err) {
          // El comprobante es opcional; no bloquear el flujo, pero notificar al usuario
          if (process.env.NODE_ENV !== 'production') {
            console.warn('No se pudo descargar el comprobante automáticamente:', err?.message);
          }
          toast.warning('Inscripción guardada, pero no se pudo descargar el comprobante. Usa el botón Imprimir en el listado.');
        }
      }
    } catch (error) {
      const msg = error.response?.data?.detail;
      if (Array.isArray(msg)) {
        toast.error(msg[0]?.msg || 'Error de validación');
      } else {
        toast.error(msg || 'Error al guardar inscripción');
      }
    }
  };

  const handleFotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('La imagen no debe superar 5MB');
        return;
      }
      setFotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setFotoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleAprobar = async (inscripcion) => {
    try {
      await aprobarInscripcion(inscripcion.id);
      toast.success('Inscripción aprobada');
      fetchInscripciones();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al aprobar');
    }
  };

  const handleIntegrar = async (inscripcion) => {
    try {
      await integrarInscripcion(inscripcion.id);
      toast.success('Alumno integrado al sistema');
      fetchInscripciones();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al integrar');
    }
  };

  const handleGenerarCredencial = async (inscripcion) => {
    try {
      const response = await generarCredencial(inscripcion.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `credencial_${inscripcion.matricula}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Credencial generada');
    } catch (error) {
      toast.error('Error al generar credencial');
    }
  };

  const handleImprimirComprobante = async (inscripcion) => {
    try {
      const response = await comprobanteInscripcion(inscripcion.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `comprobante_inscripcion_${inscripcion.matricula}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Comprobante descargado');
    } catch (error) {
      toast.error('Error al generar comprobante');
    }
  };

  const handlePrecargarPorCurp = async () => {
    const curp = (formData.curp || '').toUpperCase().trim();
    if (!curp || curp.length < 18) {
      toast.error('Captura el CURP completo (18 caracteres) antes de precargar');
      return;
    }
    try {
      const resp = await precargarInscripcionByCurp(curp);
      const { encontrado_en, datos } = resp.data || {};
      if (!datos) {
        toast.info('No se encontró un registro previo para este CURP');
        return;
      }
      const tutor = datos.tutor || {};
      const emerg = datos.contacto_emergencia || {};
      setFormData((p) => ({
        ...p,
        nombre: datos.nombre || p.nombre,
        apellido_paterno: datos.apellido_paterno || p.apellido_paterno,
        apellido_materno: datos.apellido_materno || p.apellido_materno,
        fecha_nacimiento: datos.fecha_nacimiento || p.fecha_nacimiento,
        nacionalidad: datos.nacionalidad || p.nacionalidad,
        estatura: datos.estatura || p.estatura,
        peso: datos.peso || p.peso,
        domicilio: datos.domicilio || p.domicilio,
        colonia: datos.colonia || p.colonia,
        codigo_postal: datos.codigo_postal || p.codigo_postal,
        municipio: datos.municipio || p.municipio,
        telefono_contacto: datos.telefono_contacto || p.telefono_contacto,
        tipo_sangre: datos.tipo_sangre || p.tipo_sangre,
        escuela_procedencia: datos.escuela_procedencia || p.escuela_procedencia,
        promedio_primaria: datos.promedio_primaria || p.promedio_primaria,
        condiciones_medicas: datos.condiciones_medicas || p.condiciones_medicas,
        turno: datos.turno || p.turno,
        // Tutor
        tutor_nombre: tutor.nombre || p.tutor_nombre,
        tutor_apellidos: tutor.apellidos || p.tutor_apellidos,
        tutor_escolaridad: tutor.escolaridad || p.tutor_escolaridad,
        tutor_ocupacion: tutor.ocupacion || p.tutor_ocupacion,
        tutor_estado_civil: tutor.estado_civil || p.tutor_estado_civil,
        tutor_email: tutor.email || p.tutor_email,
        tutor_telefono_casa: tutor.telefono_casa || p.tutor_telefono_casa,
        tutor_telefono_celular: tutor.telefono_celular || p.tutor_telefono_celular,
        tutor_direccion: tutor.direccion || p.tutor_direccion,
        tutor_colonia: tutor.colonia || p.tutor_colonia,
        tutor_municipio: tutor.municipio || p.tutor_municipio,
        tutor_codigo_postal: tutor.codigo_postal || p.tutor_codigo_postal,
        // Emergencia
        emergencia_nombre: emerg.nombre_completo || p.emergencia_nombre,
        emergencia_parentesco: emerg.parentesco || p.emergencia_parentesco,
        emergencia_telefono: emerg.telefono || p.emergencia_telefono,
        emergencia_traslado: emerg.traslado_emergencia || p.emergencia_traslado,
      }));
      toast.success(`Datos precargados desde ${encontrado_en === 'alumnos' ? 'el padrón de alumnos' : 'inscripciones previas'}. Revísalos antes de guardar.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al precargar datos');
    }
  };

  const handleUploadBulk = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedCiclo) return;
    
    try {
      const response = await bulkUploadInscripciones(file, selectedCiclo.id);
      toast.success(`${response.data.created} inscripciones cargadas`);
      if (response.data.errors?.length > 0) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('Errores en carga masiva:', response.data.errors);
        }
        toast.warning(`${response.data.errors.length} errores encontrados`);
      }
      fetchInscripciones();
    } catch (error) {
      toast.error('Error al cargar archivo');
    }
    e.target.value = '';
  };

  const handleExportExcel = async () => {
    try {
      const params = { ciclo_escolar_id: selectedCiclo?.id };
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== 'all') params[key] = value;
      });
      
      const response = await exportInscripcionesExcel(params);
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'inscripciones.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Reporte exportado');
    } catch (error) {
      toast.error('Error al exportar');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await downloadInscripcionesTemplate();
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'template_inscripciones.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Error al descargar plantilla');
    }
  };

  const handlePromocionPreview = async () => {
    if (!selectedCiclo || ciclos.length < 2) {
      toast.error('Se necesitan al menos 2 ciclos escolares');
      return;
    }
    
    const cicloDestino = ciclos.find(c => c.id !== selectedCiclo.id);
    if (!cicloDestino) {
      toast.error('No hay ciclo destino disponible');
      return;
    }
    
    try {
      const response = await promoverCicloEscolar({
        ciclo_origen_id: selectedCiclo.id,
        ciclo_destino_id: cicloDestino.id,
        confirmar: false
      });
      setPromocionPreview({ ...response.data, ciclo_destino_id: cicloDestino.id });
      setShowPromocionModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al obtener preview');
    }
  };

  const handlePromocionConfirmar = async () => {
    if (!promocionPreview) return;
    
    try {
      const response = await promoverCicloEscolar({
        ciclo_origen_id: selectedCiclo.id,
        ciclo_destino_id: promocionPreview.ciclo_destino_id,
        confirmar: true
      });
      toast.success(response.data.mensaje);
      setShowPromocionModal(false);
      setPromocionPreview(null);
      fetchInscripciones();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error en promoción');
    }
  };

  const openEditModal = async (inscripcion) => {
    try {
      const response = await getInscripcion(inscripcion.id);
      const data = response.data;
      const s = (v) => (v === null || v === undefined ? '' : v);
      
      setFormData({
        matricula: s(data.matricula),
        nombre: s(data.nombre),
        apellido_paterno: s(data.apellido_paterno),
        apellido_materno: s(data.apellido_materno),
        curp: s(data.curp),
        fecha_nacimiento: s(data.fecha_nacimiento),
        nacionalidad: data.nacionalidad || 'Mexicana',
        estatura: s(data.estatura),
        peso: s(data.peso),
        domicilio: s(data.domicilio),
        colonia: s(data.colonia),
        codigo_postal: s(data.codigo_postal),
        municipio: s(data.municipio),
        telefono_contacto: s(data.telefono_contacto),
        tipo_sangre: s(data.tipo_sangre),
        escuela_procedencia: s(data.escuela_procedencia),
        promedio_primaria: data.promedio_primaria?.toString() || '',
        condiciones_medicas: s(data.condiciones_medicas),
        turno: s(data.turno),
        grupo: s(data.grupo),
        ciclo_escolar_id: s(data.ciclo_escolar_id),
        tutor_nombre: s(data.tutor?.nombre),
        tutor_apellidos: s(data.tutor?.apellidos),
        tutor_escolaridad: s(data.tutor?.escolaridad),
        tutor_ocupacion: s(data.tutor?.ocupacion),
        tutor_estado_civil: s(data.tutor?.estado_civil),
        tutor_email: s(data.tutor?.email),
        tutor_telefono_casa: s(data.tutor?.telefono_casa),
        tutor_telefono_celular: s(data.tutor?.telefono_celular),
        tutor_direccion: s(data.tutor?.direccion),
        tutor_colonia: s(data.tutor?.colonia),
        tutor_municipio: s(data.tutor?.municipio),
        tutor_codigo_postal: s(data.tutor?.codigo_postal),
        emergencia_nombre: s(data.contacto_emergencia?.nombre_completo),
        emergencia_parentesco: s(data.contacto_emergencia?.parentesco),
        emergencia_telefono: s(data.contacto_emergencia?.telefono),
        emergencia_traslado: s(data.contacto_emergencia?.traslado_emergencia)
      });
      
      setSelectedInscripcion(data);
      setEditMode(true);
      setActiveTab('datos');
      setShowModal(true);
    } catch (error) {
      toast.error('Error al cargar inscripción');
    }
  };

  const openDetailModal = async (inscripcion) => {
    try {
      const response = await getInscripcion(inscripcion.id);
      setSelectedInscripcion(response.data);
      setShowDetailModal(true);
    } catch (error) {
      toast.error('Error al cargar detalles');
    }
  };

  const resetForm = () => {
    setFormData({
      ...initialFormData,
      ciclo_escolar_id: selectedCiclo?.id || ''
    });
    setSelectedInscripcion(null);
    setEditMode(false);
    setFotoFile(null);
    setFotoPreview(null);
    setActiveTab('datos');
  };

  const getEstatusColor = (estatus) => {
    const colors = {
      pendiente: 'bg-yellow-100 text-yellow-800',
      completada: 'bg-blue-100 text-blue-800',
      aprobada: 'bg-green-100 text-green-800',
      rechazada: 'bg-red-100 text-red-800',
      integrada: 'bg-purple-100 text-purple-800'
    };
    return colors[estatus] || 'bg-gray-100 text-gray-800';
  };

  const handleDelete = async (motivo) => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteInscripcion(deleteTarget.id, motivo);
      toast.success(`Inscripción de ${deleteTarget.nombre || deleteTarget.matricula} eliminada`);
      setDeleteTarget(null);
      fetchInscripciones();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo eliminar la inscripción');
    } finally {
      setDeleting(false);
    }
  };

  const handleReinsBuscarCurp = async () => {
    const curp = reinsCurp.trim().toUpperCase();
    if (curp.length < 6) {
      toast.error('Ingresa el CURP del alumno (mínimo 6 caracteres)');
      return;
    }
    setReinsLoading(true);
    setReinsPreload(null);
    try {
      const resp = await precargarInscripcionByCurp(curp);
      const data = resp.data;
      if (data.encontrado_en !== 'alumnos') {
        toast.warning('No se encontró un alumno activo con ese CURP. Usa "Nueva Inscripción" para alumnos nuevos.');
        return;
      }
      if (data.ya_en_ciclo_activo) {
        toast.error('Este alumno ya tiene una inscripción registrada en el ciclo activo. Puedes editarla desde la lista de inscripciones.');
        return;
      }
      if (!data.ciclo_destino_activo) {
        toast.error('No hay un ciclo escolar activo configurado. Actívalo desde Configuración → Ciclos escolares.');
        return;
      }
      if (!data.puede_reinscribirse) {
        toast.error(`El alumno no puede reinscribirse (${data.estatus_actual || 'sin estatus'}, grado actual ${data.grado_actual || '?'}).`);
        return;
      }
      setReinsGrupo(data.grupo_actual || 'A');
      setReinsPreload(data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error buscando alumno');
    } finally {
      setReinsLoading(false);
    }
  };

  const handleReinsConfirmar = async () => {
    if (!reinsPreload?.alumno_id) return;
    setReinsLoading(true);
    try {
      const resp = await crearReinscripcion({
        alumno_id: reinsPreload.alumno_id,
        grupo: reinsGrupo,
      });
      toast.success(`Reinscripción creada — grado ${resp.data.grado_destino}° grupo ${resp.data.grupo}. Abriendo edición para actualizar datos…`);
      setShowReinscripcionModal(false);
      setReinsCurp('');
      setReinsPreload(null);
      // Cargar la nueva inscripción y abrir modal de edición para completar/actualizar datos
      try {
        const newInsc = await getInscripcion(resp.data.inscripcion_id);
        openEditModal(newInsc.data);
      } catch {
        fetchInscripciones();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear la reinscripción');
    } finally {
      setReinsLoading(false);
    }
  };

  const getEstatusIcon = (estatus) => {
    switch (estatus) {
      case 'pendiente': return <Clock size={14} />;
      case 'completada': return <CheckCircle size={14} />;
      case 'aprobada': return <Check size={14} />;
      case 'rechazada': return <XCircle size={14} />;
      case 'integrada': return <Users size={14} />;
      default: return null;
    }
  };

  const calcularProgreso = () => {
    let campos = 0;
    let completados = 0;
    
    // Datos del alumno (15 campos obligatorios)
    const camposAlumno = ['matricula', 'nombre', 'apellido_paterno', 'apellido_materno', 'curp', 
                         'fecha_nacimiento', 'domicilio', 'colonia', 'codigo_postal', 'municipio',
                         'telefono_contacto', 'escuela_procedencia', 'promedio_primaria', 'turno', 'grupo'];
    camposAlumno.forEach(c => {
      campos++;
      if (formData[c]) completados++;
    });
    
    // Tutor (2 obligatorios)
    if (formData.tutor_nombre) completados++;
    if (formData.tutor_apellidos) completados++;
    if (formData.tutor_telefono_celular) completados++;
    campos += 3;
    
    // Emergencia (3 obligatorios)
    if (formData.emergencia_nombre) completados++;
    if (formData.emergencia_parentesco) completados++;
    if (formData.emergencia_telefono) completados++;
    campos += 3;
    
    return Math.round((completados / campos) * 100);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="inscripciones-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold">Inscripciones</h1>
          <p className="text-muted-foreground">Registro y administración de alumnos de nuevo ingreso</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={selectedCiclo?.id || ''} onValueChange={(v) => {
            const ciclo = ciclos.find(c => c.id === v);
            setSelectedCiclo(ciclo);
            setFormData(prev => ({ ...prev, ciclo_escolar_id: v }));
          }}>
            <SelectTrigger className="w-[200px]" data-testid="ciclo-select">
              <SelectValue placeholder="Seleccionar ciclo" />
            </SelectTrigger>
            <SelectContent>
              {ciclos.map(c => (
                <SelectItem key={c.id} value={c.id}>
                  {c.nombre} {c.activo && '(Activo)'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button variant="outline" size="sm" onClick={handleDownloadTemplate}>
            <Download size={16} className="mr-1" />
            Plantilla
          </Button>
          
          <label className="cursor-pointer">
            <Button variant="outline" size="sm" asChild>
              <span>
                <Upload size={16} className="mr-1" />
                Carga Masiva
              </span>
            </Button>
            <input type="file" className="hidden" accept=".xlsx,.xls" onChange={handleUploadBulk} />
          </label>
          
          <Button variant="outline" size="sm" onClick={handleExportExcel}>
            <FileXls size={16} className="mr-1" />
            Exportar
          </Button>
          
          {user?.rol === 'administrador' && (
            <Button variant="outline" size="sm" onClick={handlePromocionPreview}>
              <ArrowsClockwise size={16} className="mr-1" />
              Promoción Ciclo
            </Button>
          )}
          
          <Button
            size="sm"
            onClick={() => { resetForm(); setShowModal(true); }}
            disabled={!canEditInscripciones}
            title={!canEditInscripciones ? 'No tienes permiso para crear inscripciones' : 'Registrar nueva inscripción'}
            data-testid="add-inscripcion"
          >
            <Plus size={16} className="mr-1" />
            Nueva Inscripción
          </Button>

          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowReinscripcionModal(true)}
            disabled={!canEditInscripciones}
            title={!canEditInscripciones ? 'No tienes permiso' : 'Reinscribir alumno existente (promoción de ciclo)'}
            data-testid="open-reinscripcion"
          >
            <ArrowsClockwise size={16} className="mr-1" />
            Reinscribir alumno
          </Button>
        </div>
      </div>

      {/* Estadísticas */}
      {estadisticas && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold">{estadisticas.total}</p>
              <p className="text-sm text-muted-foreground">Total</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-yellow-600">{estadisticas.por_estatus.pendientes}</p>
              <p className="text-sm text-muted-foreground">Pendientes</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-blue-600">{estadisticas.por_estatus.completadas}</p>
              <p className="text-sm text-muted-foreground">Completadas</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-green-600">{estadisticas.por_estatus.aprobadas}</p>
              <p className="text-sm text-muted-foreground">Aprobadas</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-purple-600">{estadisticas.por_estatus.integradas}</p>
              <p className="text-sm text-muted-foreground">Integradas</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-red-600">{estadisticas.por_estatus.rechazadas}</p>
              <p className="text-sm text-muted-foreground">Rechazadas</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filtros y búsqueda */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
              <Input
                placeholder="Buscar por nombre, matrícula o CURP..."
                value={busqueda}
                onChange={(e) => { setBusqueda(e.target.value); setPage(1); }}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <div className="flex gap-2">
              <Select value={filters.turno} onValueChange={(v) => handleFilterChange('turno', v)}>
                <SelectTrigger className="w-[130px]">
                  <SelectValue placeholder="Turno" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="matutino">Matutino</SelectItem>
                  <SelectItem value="vespertino">Vespertino</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={filters.grupo} onValueChange={(v) => handleFilterChange('grupo', v)}>
                <SelectTrigger className="w-[110px]">
                  <SelectValue placeholder="Grupo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="A">A</SelectItem>
                  <SelectItem value="B">B</SelectItem>
                  <SelectItem value="C">C</SelectItem>
                  <SelectItem value="D">D</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={filters.estatus} onValueChange={(v) => handleFilterChange('estatus', v)}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Estatus" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="pendiente">Pendiente</SelectItem>
                  <SelectItem value="completada">Completada</SelectItem>
                  <SelectItem value="aprobada">Aprobada</SelectItem>
                  <SelectItem value="integrada">Integrada</SelectItem>
                  <SelectItem value="rechazada">Rechazada</SelectItem>
                </SelectContent>
              </Select>
              
              {(filters.turno || filters.grupo || filters.estatus || busqueda) && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X size={16} />
                </Button>
              )}
            </div>
          </div>

          {hasPermission(['administrador', 'direccion']) && (
            <div className="flex items-center gap-2 pt-3 mt-3 border-t">
              <input
                id="incluir-egresados-insc"
                type="checkbox"
                className="h-4 w-4 rounded border-input"
                checked={filters.incluir_egresados}
                onChange={(e) => setFilters(prev => ({ ...prev, incluir_egresados: e.target.checked }))}
                data-testid="toggle-egresados-inscripciones"
              />
              <Label htmlFor="incluir-egresados-insc" className="cursor-pointer text-sm">
                Mostrar inscripciones de alumnos <strong>egresados</strong> (archivados)
              </Label>
              {filters.incluir_egresados && (
                <Badge variant="secondary" className="ml-2 text-xs">Vista de archivo activa</Badge>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabla de inscripciones */}
      <Card>
        <CardHeader className="py-4">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <UserPlus size={20} />
            Lista de Inscripciones ({total})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <ScrollArea className="w-full">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead>Matrícula</TableHead>
                  <TableHead>Nombre Completo</TableHead>
                  <TableHead>CURP</TableHead>
                  <TableHead className="w-[100px]">Turno</TableHead>
                  <TableHead className="w-[80px]">Grupo</TableHead>
                  <TableHead className="w-[120px]">Estatus</TableHead>
                  <TableHead className="w-[150px] text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <div className="spinner mx-auto" />
                    </TableCell>
                  </TableRow>
                ) : inscripciones.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No hay inscripciones registradas
                    </TableCell>
                  </TableRow>
                ) : (
                  inscripciones.map((inscripcion) => (
                    <TableRow key={inscripcion.id} className="hover:bg-muted/30">
                      <TableCell className="font-mono text-sm">{inscripcion.matricula}</TableCell>
                      <TableCell className="font-medium min-w-[160px] max-w-[260px]">
                        <span
                          className="text-xs sm:text-sm md:text-base break-words whitespace-normal leading-tight block"
                          title={`${inscripcion.nombre || ''} ${inscripcion.apellido_paterno || ''} ${inscripcion.apellido_materno || ''}`.trim()}
                        >
                          {inscripcion.nombre} {inscripcion.apellido_paterno} {inscripcion.apellido_materno}
                        </span>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{inscripcion.curp}</TableCell>
                      <TableCell className="capitalize">{inscripcion.turno}</TableCell>
                      <TableCell>{inscripcion.grupo}</TableCell>
                      <TableCell>
                        <Badge className={`text-xs flex items-center gap-1 w-fit ${getEstatusColor(inscripcion.estatus)}`}>
                          {getEstatusIcon(inscripcion.estatus)}
                          {inscripcion.estatus}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openDetailModal(inscripcion)} title="Ver detalles">
                            <Eye size={16} />
                          </Button>
                          
                          {inscripcion.estatus !== 'integrada' && canEditInscripciones && (
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditModal(inscripcion)} title="Editar" data-testid={`edit-inscripcion-${inscripcion.matricula}`}>
                              <PencilSimple size={16} />
                            </Button>
                          )}
                          
                          {inscripcion.estatus === 'completada' && (
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-green-600" onClick={() => handleAprobar(inscripcion)} title="Aprobar">
                              <Check size={16} />
                            </Button>
                          )}
                          
                          {inscripcion.estatus === 'aprobada' && (
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-purple-600" onClick={() => handleIntegrar(inscripcion)} title="Integrar al sistema">
                              <Users size={16} />
                            </Button>
                          )}
                          
                          {(inscripcion.estatus === 'aprobada' || inscripcion.estatus === 'integrada') && (
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-primary" onClick={() => handleGenerarCredencial(inscripcion)} title="Generar credencial">
                              <IdentificationCard size={16} />
                            </Button>
                          )}

                          <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600" onClick={() => handleImprimirComprobante(inscripcion)} title="Imprimir comprobante de inscripción" data-testid={`comprobante-${inscripcion.matricula}`}>
                            <Printer size={16} />
                          </Button>

                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive"
                              onClick={() => setDeleteTarget(inscripcion)}
                              title="Eliminar inscripción (irreversible)"
                              data-testid={`delete-inscripcion-${inscripcion.matricula}`}
                            >
                              <Trash size={16} />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Modal de Inscripción */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <UserPlus size={24} />
              {editMode ? 'Editar Inscripción' : 'Nueva Inscripción'}
            </DialogTitle>
            <DialogDescription>
              Complete todos los datos del alumno de nuevo ingreso
            </DialogDescription>
          </DialogHeader>
          
          <div className="mb-4">
            <div className="flex items-center justify-between text-sm mb-2">
              <span>Progreso del registro</span>
              <span className="font-medium">{calcularProgreso()}%</span>
            </div>
            <Progress value={calcularProgreso()} className="h-2" />
          </div>
          
          <form onSubmit={handleSubmit}>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-3 mb-4">
                <TabsTrigger value="datos">Datos del Alumno</TabsTrigger>
                <TabsTrigger value="tutor">Datos del Tutor</TabsTrigger>
                <TabsTrigger value="emergencia">Contacto Emergencia</TabsTrigger>
              </TabsList>
              
              {/* Tab 1: Datos del Alumno */}
              <TabsContent value="datos" className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {/* Foto */}
                  <div className="md:row-span-3">
                    <Label>Foto del Alumno</Label>
                    <div className="mt-2 border-2 border-dashed rounded-lg p-4 text-center">
                      {fotoPreview ? (
                        <img src={fotoPreview} alt="Preview" className="w-full h-40 object-cover rounded" />
                      ) : (
                        <div className="h-40 flex items-center justify-center bg-muted rounded">
                          <Student size={48} className="text-muted-foreground" />
                        </div>
                      )}
                      <label className="cursor-pointer">
                        <Button type="button" variant="outline" size="sm" className="mt-2" asChild>
                          <span>
                            <Upload size={14} className="mr-1" />
                            Subir Foto
                          </span>
                        </Button>
                        <input type="file" className="hidden" accept="image/*" onChange={handleFotoChange} />
                      </label>
                    </div>
                  </div>
                  
                  {/* Datos básicos */}
                  <div className="md:col-span-3 grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <Label>Matrícula *</Label>
                      <Input value={formData.matricula} onChange={(e) => setFormData(p => ({ ...p, matricula: e.target.value }))} required disabled={editMode} data-testid="form-matricula" />
                    </div>
                    <div>
                      <Label>CURP *</Label>
                      <div className="flex gap-2">
                        <Input value={formData.curp} onChange={(e) => setFormData(p => ({ ...p, curp: e.target.value.toUpperCase() }))} required maxLength={18} placeholder="AAAA000000HAAAAA00" data-testid="form-curp" />
                        {!editMode && (
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={handlePrecargarPorCurp}
                            title="Precargar datos desde alumnos / inscripciones previas"
                            data-testid="precargar-curp-btn"
                            className="shrink-0"
                          >
                            <MagicWand size={16} />
                          </Button>
                        )}
                      </div>
                      {!editMode && (
                        <p className="text-xs text-muted-foreground mt-1">Si el alumno ya estuvo registrado (promoción de ciclo), pulsa el icono para precargar sus datos.</p>
                      )}
                    </div>
                    <div>
                      <Label>Fecha de Nacimiento *</Label>
                      <Input type="date" value={formData.fecha_nacimiento} onChange={(e) => setFormData(p => ({ ...p, fecha_nacimiento: e.target.value }))} required />
                    </div>
                    <div>
                      <Label>Nombre(s) *</Label>
                      <Input value={formData.nombre} onChange={(e) => setFormData(p => ({ ...p, nombre: e.target.value }))} required data-testid="form-nombre" />
                    </div>
                    <div>
                      <Label>Apellido Paterno *</Label>
                      <Input value={formData.apellido_paterno} onChange={(e) => setFormData(p => ({ ...p, apellido_paterno: e.target.value }))} required />
                    </div>
                    <div>
                      <Label>Apellido Materno *</Label>
                      <Input value={formData.apellido_materno} onChange={(e) => setFormData(p => ({ ...p, apellido_materno: e.target.value }))} required />
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <Label>Nacionalidad</Label>
                    <Input value={formData.nacionalidad} onChange={(e) => setFormData(p => ({ ...p, nacionalidad: e.target.value }))} />
                  </div>
                  <div>
                    <Label>Tipo de Sangre</Label>
                    <Select value={formData.tipo_sangre} onValueChange={(v) => setFormData(p => ({ ...p, tipo_sangre: v }))}>
                      <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                      <SelectContent>
                        {TIPOS_SANGRE.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Estatura (m)</Label>
                    <Input value={formData.estatura} onChange={(e) => setFormData(p => ({ ...p, estatura: e.target.value }))} placeholder="1.55" />
                  </div>
                  <div>
                    <Label>Peso (kg)</Label>
                    <Input value={formData.peso} onChange={(e) => setFormData(p => ({ ...p, peso: e.target.value }))} placeholder="50" />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Domicilio *</Label>
                    <Input value={formData.domicilio} onChange={(e) => setFormData(p => ({ ...p, domicilio: e.target.value }))} required />
                  </div>
                  <div>
                    <Label>Colonia *</Label>
                    <Input value={formData.colonia} onChange={(e) => setFormData(p => ({ ...p, colonia: e.target.value }))} required />
                  </div>
                  <div>
                    <Label>Código Postal *</Label>
                    <Input value={formData.codigo_postal} onChange={(e) => setFormData(p => ({ ...p, codigo_postal: e.target.value }))} required maxLength={5} />
                  </div>
                  <div>
                    <Label>Municipio *</Label>
                    <Input value={formData.municipio} onChange={(e) => setFormData(p => ({ ...p, municipio: e.target.value }))} required />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <Label>Teléfono de Contacto *</Label>
                    <Input value={formData.telefono_contacto} onChange={(e) => setFormData(p => ({ ...p, telefono_contacto: e.target.value }))} required maxLength={10} />
                  </div>
                  <div>
                    <Label>Escuela de Procedencia *</Label>
                    <Input value={formData.escuela_procedencia} onChange={(e) => setFormData(p => ({ ...p, escuela_procedencia: e.target.value }))} required />
                  </div>
                  <div>
                    <Label>Promedio Primaria *</Label>
                    <Input type="number" step="0.1" min="0" max="10" value={formData.promedio_primaria} onChange={(e) => setFormData(p => ({ ...p, promedio_primaria: e.target.value }))} required />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Turno *</Label>
                    <Select value={formData.turno} onValueChange={(v) => setFormData(p => ({ ...p, turno: v }))} required>
                      <SelectTrigger data-testid="form-turno"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="matutino">Matutino</SelectItem>
                        <SelectItem value="vespertino">Vespertino</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Grupo *</Label>
                    <Select value={formData.grupo} onValueChange={(v) => setFormData(p => ({ ...p, grupo: v }))} required>
                      <SelectTrigger data-testid="form-grupo"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="A">A</SelectItem>
                        <SelectItem value="B">B</SelectItem>
                        <SelectItem value="C">C</SelectItem>
                        <SelectItem value="D">D</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div>
                  <Label>Condiciones Médicas</Label>
                  <Textarea value={formData.condiciones_medicas} onChange={(e) => setFormData(p => ({ ...p, condiciones_medicas: e.target.value }))} placeholder="Alergias, medicamentos, condiciones especiales..." />
                </div>
              </TabsContent>
              
              {/* Tab 2: Datos del Tutor */}
              <TabsContent value="tutor" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nombre(s) *</Label>
                    <Input value={formData.tutor_nombre} onChange={(e) => setFormData(p => ({ ...p, tutor_nombre: e.target.value }))} required />
                  </div>
                  <div>
                    <Label>Apellidos *</Label>
                    <Input value={formData.tutor_apellidos} onChange={(e) => setFormData(p => ({ ...p, tutor_apellidos: e.target.value }))} required />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <Label>Escolaridad</Label>
                    <Input value={formData.tutor_escolaridad} onChange={(e) => setFormData(p => ({ ...p, tutor_escolaridad: e.target.value }))} />
                  </div>
                  <div>
                    <Label>Ocupación</Label>
                    <Input value={formData.tutor_ocupacion} onChange={(e) => setFormData(p => ({ ...p, tutor_ocupacion: e.target.value }))} />
                  </div>
                  <div>
                    <Label>Estado Civil</Label>
                    <Select value={formData.tutor_estado_civil} onValueChange={(v) => setFormData(p => ({ ...p, tutor_estado_civil: v }))}>
                      <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                      <SelectContent>
                        {ESTADOS_CIVILES.map(e => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Correo Electrónico</Label>
                    <Input type="email" value={formData.tutor_email} onChange={(e) => setFormData(p => ({ ...p, tutor_email: e.target.value }))} />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Teléfono Casa</Label>
                    <Input value={formData.tutor_telefono_casa} onChange={(e) => setFormData(p => ({ ...p, tutor_telefono_casa: e.target.value }))} maxLength={10} />
                  </div>
                  <div>
                    <Label>Teléfono Celular *</Label>
                    <Input value={formData.tutor_telefono_celular} onChange={(e) => setFormData(p => ({ ...p, tutor_telefono_celular: e.target.value }))} required maxLength={10} />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Dirección</Label>
                    <Input value={formData.tutor_direccion} onChange={(e) => setFormData(p => ({ ...p, tutor_direccion: e.target.value }))} />
                  </div>
                  <div>
                    <Label>Colonia</Label>
                    <Input value={formData.tutor_colonia} onChange={(e) => setFormData(p => ({ ...p, tutor_colonia: e.target.value }))} />
                  </div>
                  <div>
                    <Label>Municipio</Label>
                    <Input value={formData.tutor_municipio} onChange={(e) => setFormData(p => ({ ...p, tutor_municipio: e.target.value }))} />
                  </div>
                  <div>
                    <Label>Código Postal</Label>
                    <Input value={formData.tutor_codigo_postal} onChange={(e) => setFormData(p => ({ ...p, tutor_codigo_postal: e.target.value }))} maxLength={5} />
                  </div>
                </div>
              </TabsContent>
              
              {/* Tab 3: Contacto de Emergencia */}
              <TabsContent value="emergencia" className="space-y-4">
                <Card className="border-destructive/20">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2 text-destructive">
                      <Warning size={20} />
                      Contacto de Emergencia
                    </CardTitle>
                    <CardDescription>
                      Persona a contactar en caso de emergencia (diferente al tutor)
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label>Nombre Completo *</Label>
                      <Input value={formData.emergencia_nombre} onChange={(e) => setFormData(p => ({ ...p, emergencia_nombre: e.target.value }))} required />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Parentesco *</Label>
                        <Input value={formData.emergencia_parentesco} onChange={(e) => setFormData(p => ({ ...p, emergencia_parentesco: e.target.value }))} required placeholder="Tío, Abuela, Vecino..." />
                      </div>
                      <div>
                        <Label>Teléfono *</Label>
                        <Input value={formData.emergencia_telefono} onChange={(e) => setFormData(p => ({ ...p, emergencia_telefono: e.target.value }))} required maxLength={10} />
                      </div>
                    </div>
                    <div>
                      <Label>Traslado en caso de emergencia</Label>
                      <Input
                        value={formData.emergencia_traslado}
                        onChange={(e) => setFormData(p => ({ ...p, emergencia_traslado: e.target.value }))}
                        placeholder="Ej. Hospital General de Ocotlán, Cruz Roja Mexicana..."
                        data-testid="form-emergencia-traslado"
                      />
                      <p className="text-xs text-muted-foreground mt-1">Indica el hospital o clínica a donde debe ser trasladado el alumno en caso de una emergencia médica.</p>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
            
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancelar
              </Button>
              <Button type="submit" data-testid="submit-inscripcion">
                {editMode ? 'Guardar Cambios' : 'Registrar Inscripción'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal de Detalle */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Detalle de Inscripción</DialogTitle>
          </DialogHeader>
          
          {selectedInscripcion && (
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-24 h-32 bg-muted rounded-lg flex items-center justify-center">
                  {selectedInscripcion.foto_path ? (
                    <img src={`${process.env.REACT_APP_BACKEND_URL}/api/inscripciones/${selectedInscripcion.id}/foto?auth=${localStorage.getItem('token')}`} alt="Foto" className="w-full h-full object-cover rounded-lg" />
                  ) : (
                    <Student size={40} className="text-muted-foreground" />
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold">
                    {selectedInscripcion.nombre} {selectedInscripcion.apellido_paterno} {selectedInscripcion.apellido_materno}
                  </h3>
                  <p className="text-muted-foreground font-mono">{selectedInscripcion.matricula}</p>
                  <Badge className={`mt-2 ${getEstatusColor(selectedInscripcion.estatus)}`}>
                    {selectedInscripcion.estatus}
                  </Badge>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">CURP</p>
                  <p className="font-mono">{selectedInscripcion.curp}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Fecha de Nacimiento</p>
                  <p>{selectedInscripcion.fecha_nacimiento}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Turno</p>
                  <p className="capitalize">{selectedInscripcion.turno}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Grupo</p>
                  <p>{selectedInscripcion.grupo}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Escuela de Procedencia</p>
                  <p>{selectedInscripcion.escuela_procedencia}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Promedio Primaria</p>
                  <p>{selectedInscripcion.promedio_primaria}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-muted-foreground">Domicilio</p>
                  <p>{selectedInscripcion.domicilio}, {selectedInscripcion.colonia}, {selectedInscripcion.municipio} C.P. {selectedInscripcion.codigo_postal}</p>
                </div>
              </div>
              
              {selectedInscripcion.tutor && (
                <Card>
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm">Datos del Tutor</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p><strong>Nombre:</strong> {selectedInscripcion.tutor.nombre} {selectedInscripcion.tutor.apellidos}</p>
                    <p><strong>Teléfono:</strong> {selectedInscripcion.tutor.telefono_celular}</p>
                    {selectedInscripcion.tutor.email && <p><strong>Email:</strong> {selectedInscripcion.tutor.email}</p>}
                  </CardContent>
                </Card>
              )}
              
              {selectedInscripcion.contacto_emergencia && (
                <Card className="border-destructive/20">
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm text-destructive">Contacto de Emergencia</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <p><strong>Nombre:</strong> {selectedInscripcion.contacto_emergencia.nombre_completo}</p>
                    <p><strong>Parentesco:</strong> {selectedInscripcion.contacto_emergencia.parentesco}</p>
                    <p><strong>Teléfono:</strong> {selectedInscripcion.contacto_emergencia.telefono}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Promoción de Ciclo */}
      <Dialog open={showPromocionModal} onOpenChange={setShowPromocionModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-amber-600">
              <ArrowsClockwise size={24} />
              Promoción de Ciclo Escolar
            </DialogTitle>
            <DialogDescription>
              Esta acción moverá automáticamente a los alumnos al siguiente grado
            </DialogDescription>
          </DialogHeader>
          
          {promocionPreview && (
            <div className="space-y-4">
              <Card className="bg-amber-50 border-amber-200">
                <CardContent className="p-4">
                  <p className="text-sm text-amber-800 mb-2">
                    <strong>Ciclo origen:</strong> {promocionPreview.ciclo_origen}
                  </p>
                  <p className="text-sm text-amber-800">
                    <strong>Ciclo destino:</strong> {promocionPreview.ciclo_destino}
                  </p>
                </CardContent>
              </Card>
              
              <div className="space-y-2">
                <p className="text-sm font-medium">Cambios que se realizarán:</p>
                <ul className="text-sm space-y-1 pl-4">
                  <li>• Alumnos de 1° → 2°: <strong>{promocionPreview.cambios_propuestos['1_a_2']}</strong></li>
                  <li>• Alumnos de 2° → 3°: <strong>{promocionPreview.cambios_propuestos['2_a_3']}</strong></li>
                  <li>• Alumnos de 3° → Egresados: <strong>{promocionPreview.cambios_propuestos['3_a_egresados']}</strong></li>
                  <li>• Nuevos ingresos → 1°: <strong>{promocionPreview.cambios_propuestos.nuevos_ingresos}</strong></li>
                </ul>
                <p className="text-sm font-medium mt-2">
                  Total de movimientos: <strong>{promocionPreview.cambios_propuestos.total_movimientos}</strong>
                </p>
              </div>
              
              <Card className="bg-red-50 border-red-200">
                <CardContent className="p-4">
                  <p className="text-sm text-red-800 flex items-center gap-2">
                    <Warning size={16} />
                    Esta acción no se puede deshacer. Asegúrese de tener un respaldo.
                  </p>
                </CardContent>
              </Card>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowPromocionModal(false); setPromocionPreview(null); }}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handlePromocionConfirmar}>
              Confirmar Promoción
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmación de eliminación */}
      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}
        title="Eliminar inscripción"
        description="Se dará de baja la inscripción y dejará de aparecer en el listado y reportes. Esta acción es irreversible."
        itemLabel={deleteTarget ? `${deleteTarget.nombre || ''} ${deleteTarget.apellido_paterno || ''} ${deleteTarget.apellido_materno || ''} — ${deleteTarget.matricula}` : ''}
        onConfirm={handleDelete}
        loading={deleting}
      />

      {/* Modal de Reinscripción */}
      <Dialog open={showReinscripcionModal} onOpenChange={(v) => { setShowReinscripcionModal(v); if (!v) { setReinsCurp(''); setReinsPreload(null); setReinsGrupo('A'); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Reinscribir alumno (nuevo ciclo escolar)</DialogTitle>
            <DialogDescription>
              Precarga automáticamente los datos personales, tutor y contacto de un alumno existente que continúa en el nuevo ciclo escolar. <strong>Mantiene el mismo grado</strong> del alumno (la promoción de grado se realiza desde &quot;Promoción de ciclo escolar&quot;); el <strong>grupo sí puede modificarse</strong>.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>CURP del alumno *</Label>
              <div className="flex gap-2">
                <Input
                  value={reinsCurp}
                  onChange={(e) => setReinsCurp(e.target.value.toUpperCase())}
                  placeholder="18 caracteres"
                  maxLength={18}
                  className="font-mono uppercase"
                  data-testid="reins-curp-input"
                />
                <Button
                  onClick={handleReinsBuscarCurp}
                  disabled={reinsLoading || reinsCurp.trim().length < 6}
                  data-testid="reins-buscar"
                >
                  <MagnifyingGlass size={16} className="mr-1" />
                  Buscar
                </Button>
              </div>
            </div>

            {reinsPreload && (
              <Card className="bg-muted/30" data-testid="reins-preload-card">
                <CardContent className="p-4 space-y-3 text-sm">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div><span className="text-muted-foreground">Alumno:</span> <strong>{reinsPreload.datos?.nombre} {reinsPreload.datos?.apellido_paterno} {reinsPreload.datos?.apellido_materno}</strong></div>
                    <div><span className="text-muted-foreground">Matrícula:</span> <strong>{reinsPreload.matricula_actual}</strong></div>
                    <div><span className="text-muted-foreground">Grado:</span> <strong className="text-primary">{reinsPreload.grado_actual}°</strong></div>
                    <div><span className="text-muted-foreground">Grupo actual:</span> <strong>{reinsPreload.grupo_actual}</strong></div>
                    <div><span className="text-muted-foreground">Turno:</span> <strong className="capitalize">{reinsPreload.turno_actual}</strong></div>
                    <div><span className="text-muted-foreground">Estatus:</span> <Badge>{reinsPreload.estatus_actual}</Badge></div>
                  </div>

                  <div>
                    <Label className="text-xs">Grupo para el nuevo ciclo *</Label>
                    <Select value={reinsGrupo} onValueChange={setReinsGrupo}>
                      <SelectTrigger className="mt-1" data-testid="reins-grupo-select">
                        <SelectValue placeholder="Seleccionar grupo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="A">A</SelectItem>
                        <SelectItem value="B">B</SelectItem>
                        <SelectItem value="C">C</SelectItem>
                        <SelectItem value="D">D</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="rounded-md border border-primary/30 bg-primary/5 p-3 text-xs" data-testid="reins-ciclo-destino-info">
                    <p>
                      Se reinscribirá al ciclo escolar activo:{' '}
                      <strong className="text-primary">
                        {reinsPreload.ciclo_destino_activo?.nombre || '—'}
                      </strong>
                    </p>
                    <p className="mt-1 text-muted-foreground">
                      El grado ({reinsPreload.grado_actual}°) se mantiene; el grupo puede modificarse. La promoción de grado se realiza únicamente desde el flujo de &quot;Promoción de ciclo escolar&quot;.
                    </p>
                  </div>

                  <p className="text-xs text-muted-foreground pt-2">
                    Toda la información personal, tutor y contacto se copia automáticamente. Después podrás editar los campos que necesiten actualización.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReinscripcionModal(false)} disabled={reinsLoading}>
              Cancelar
            </Button>
            <Button onClick={handleReinsConfirmar} disabled={!reinsPreload || reinsLoading} data-testid="reins-confirmar">
              {reinsLoading ? 'Procesando…' : 'Crear reinscripción'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InscripcionesPage;
